import React, { useState } from 'react';
import { Mail, Send, Sparkles, ArrowLeft } from 'lucide-react';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const EmailTester = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailType, setEmailType] = useState('college_onboarding');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [payload, setPayload] = useState({
    fullName: '',
    password: '',
    collegeName: '',
    adminEmail: '',
    otp: '',
    otpType: 'login',
    role: 'college_admin',
    ipAddress: '',
    browser: '',
    os: '',
    location: ''
  });

  const emailOptions = [
    { value: 'college_onboarding', label: 'College Onboarding Confirmation' },
    { value: 'college_admin_welcome', label: 'College Admin Welcome Credentials' },
    { value: 'student_welcome', label: 'Student Welcome Credentials' },
    { value: 'login_notification', label: 'New Login Alert' },
    { value: 'password_change', label: 'Password Changed Notification' },
    { value: 'otp_email', label: 'One-Time Password (OTP) Mail' }
  ];

  const handleInputChange = (field, val) => {
    setPayload(prev => ({
      ...prev,
      [field]: val
    }));
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!recipientEmail) {
      toast.error('Recipient email is required');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/colleges/test-email', {
        emailType,
        recipientEmail,
        payload
      });
      if (res.data.success) {
        toast.success(res.data.message || 'Email sent successfully!');
        if (res.data.devBypass) {
          toast.loading('Dev bypass fallback triggered - check Node console log', { id: 'dev-bypass', duration: 4000 });
        }
      } else {
        toast.error(res.data.message || 'Failed to send email');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'An error occurred while sending email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-7 w-7 text-indigo-600" />
            Email Notification Tester
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Development Trail Page for testing SMTP configurations and email templates
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Selection Card */}
        <div className="md:col-span-1">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Configuration</h2>
            <div className="space-y-4">
              <Select
                label="Notification Type"
                value={emailType}
                onChange={(e) => setEmailType(e.target.value)}
                options={emailOptions}
                required
              />

              <Input
                label="Recipient Email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="e.g. tester@example.com"
                required
              />
            </div>
          </Card>
        </div>

        {/* Payload details Card */}
        <div className="md:col-span-2">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Dynamic Email Payload Parameters
            </h2>

            <form onSubmit={handleSendEmail} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Standard name field for most templates */}
                {['college_onboarding', 'college_admin_welcome', 'student_welcome', 'login_notification', 'password_change', 'otp_email'].includes(emailType) && (
                  <Input
                    label={emailType === 'college_onboarding' ? 'Admin Full Name' : 'Recipient Full Name'}
                    value={payload.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder="e.g. Alex Mercer"
                  />
                )}

                {/* Password field */}
                {['college_onboarding', 'college_admin_welcome', 'student_welcome'].includes(emailType) && (
                  <Input
                    label="Account Password"
                    type="text"
                    value={payload.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="e.g. SmaartPass@123"
                  />
                )}

                {/* College Name field */}
                {emailType === 'college_onboarding' && (
                  <>
                    <Input
                      label="Institution Name"
                      value={payload.collegeName}
                      onChange={(e) => handleInputChange('collegeName', e.target.value)}
                      placeholder="e.g. Harvard University"
                    />
                    <Input
                      label="Admin Login Email"
                      type="email"
                      value={payload.adminEmail}
                      onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                      placeholder="e.g. admin@harvard.edu"
                    />
                  </>
                )}

                {/* OTP fields */}
                {emailType === 'otp_email' && (
                  <>
                    <Input
                      label="OTP Code"
                      value={payload.otp}
                      onChange={(e) => handleInputChange('otp', e.target.value)}
                      placeholder="e.g. 542918"
                    />
                    <Select
                      label="OTP Type"
                      value={payload.otpType}
                      onChange={(e) => handleInputChange('otpType', e.target.value)}
                      options={[
                        { value: 'login', label: 'Login Authentication' },
                        { value: 'reset', label: 'Password Reset' }
                      ]}
                    />
                  </>
                )}

                {/* Role field */}
                {emailType === 'college_admin_welcome' && (
                  <Select
                    label="Administrator Role"
                    value={payload.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    options={[
                      { value: 'college_admin', label: 'Institute Administrator' },
                      { value: 'admin', label: 'System Administrator' }
                    ]}
                  />
                )}

                {/* Login Info details */}
                {emailType === 'login_notification' && (
                  <>
                    <Input
                      label="IP Address"
                      value={payload.ipAddress}
                      onChange={(e) => handleInputChange('ipAddress', e.target.value)}
                      placeholder="e.g. 192.168.1.50"
                    />
                    <Input
                      label="Browser"
                      value={payload.browser}
                      onChange={(e) => handleInputChange('browser', e.target.value)}
                      placeholder="e.g. Mozilla Firefox"
                    />
                    <Input
                      label="Operating System"
                      value={payload.os}
                      onChange={(e) => handleInputChange('os', e.target.value)}
                      placeholder="e.g. MacOS Monterey"
                    />
                    <Input
                      label="Location"
                      value={payload.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="e.g. Boston, MA"
                    />
                  </>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100">
                <Button
                  variant="primary"
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  Send Test Email
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailTester;
